import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { InputAdornment, TextField } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { IMaskInput } from "react-imask";

import AnimatedAmountDisplay from "./animated-amount-display";
import CustomTextField from "./text-field";

const AMOUNT_MAX = 9999.99;

export default function AmountField({ rules = {} }) {
  const { control } = useFormContext();

  return <Controller name="cash-amount" control={control} rules={rules} render={ControlledAmountField} />;
}

// Decimal-first input: "." and trailing-zero decimals (like ".0") can't
// survive IMask's normalizeZeros, so we accumulate them in pendingDecimal
// (e.g. ".", ".0") and only commit to IMask once a non-zero digit makes
// the value safe. The animated display shows the pending chars; the real
// caret is hidden until we commit.
const ControlledAmountField = ({ field }) => {
  const [rejectBump, setRejectBump] = useState(0);
  const [pendingDecimal, setPendingDecimal] = useState(""); // "", ".", ".0"
  const valueRef = useRef(field.value);
  const inputElRef = useRef(null);
  valueRef.current = field.value;

  // Clear pending once the real value includes a decimal (committed successfully)
  useEffect(() => {
    if (pendingDecimal && field.value?.masked?.includes(".")) {
      setPendingDecimal("");
      if (inputElRef.current) inputElRef.current.style.caretColor = "";
    }
  }, [pendingDecimal, field.value]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!inputElRef.current) inputElRef.current = e.target;

      if (e.key === "." || e.key === "Decimal") {
        e.preventDefault();
        const masked = valueRef.current?.masked || "";
        if (!masked.includes(".") && !pendingDecimal) {
          setPendingDecimal(".");
          e.target.style.caretColor = "transparent";
        }
      } else if (/^[0-9]$/.test(e.key)) {
        if (pendingDecimal) {
          e.preventDefault();
          const newPending = pendingDecimal + e.key;
          const curMasked = valueRef.current?.masked || "$0";
          const curUnmasked = valueRef.current?.unmasked || "0";
          const fullUnmasked = (curUnmasked || "0") + newPending;
          if (parseFloat(fullUnmasked) > AMOUNT_MAX) {
            setRejectBump((b) => b + 1);
            return;
          }
          // Non-zero digit: safe to commit (IMask won't strip it)
          if (e.key !== "0") {
            field.onChange({ masked: curMasked + newPending, unmasked: fullUnmasked });
          } else {
            // Trailing zero — keep accumulating (max 2 decimal digits)
            const decDigits = newPending.slice(1);
            if (decDigits.length >= 2) {
              // Hit max scale, commit even though IMask may normalize
              field.onChange({ masked: curMasked + newPending, unmasked: fullUnmasked });
            } else {
              setPendingDecimal(newPending);
            }
          }
          return;
        }
        const currentNum = parseFloat(valueRef.current?.unmasked || "0") || 0;
        if (currentNum * 10 > AMOUNT_MAX) {
          setRejectBump((b) => b + 1);
        }
      } else if (e.key === "Backspace" || e.key === "Delete") {
        if (pendingDecimal) {
          e.preventDefault();
          if (pendingDecimal.length <= 1) {
            // Just the dot — remove it entirely
            setPendingDecimal("");
            e.target.style.caretColor = "";
          } else {
            // ".0" → ".", ".00" → ".0"
            setPendingDecimal(pendingDecimal.slice(0, -1));
          }
        } else {
          const unmasked = valueRef.current?.unmasked || "";
          if (!unmasked || unmasked === "0") {
            setRejectBump((b) => b + 1);
          }
        }
      }
    },
    [pendingDecimal, field],
  );

  const displayValue = React.useMemo(() => {
    if (!pendingDecimal) return field.value;
    const masked = field.value?.masked || "$0";
    if (masked.includes(".")) return field.value;
    return { ...field.value, masked: masked + pendingDecimal };
  }, [field.value, pendingDecimal]);

  return (
    <div className="amount-field has-animated-display">
      <AnimatedAmountDisplay value={displayValue} rejectBump={rejectBump} showCaret={!!pendingDecimal} />
      <TextField
        {...field}
        id="cash-amount-input"
        placeholder="$0"
        autoFocus
        onKeyDown={handleKeyDown}
        inputProps={{ mask: "$num", max: AMOUNT_MAX, "aria-label": "Amount in dollars" }}
        InputProps={{
          inputComponent: AmountFieldMaskAdapter,
        }}
      />
    </div>
  );
};

export const AmountFieldMaskAdapter = React.forwardRef((props, ref) => {
  const { onChange, value, max, mask, padFractionalZeros = false, ...other } = props;

  return (
    <IMaskInput
      {...other}
      value={value?.masked}
      mask={mask}
      blocks={{
        num: {
          mask: Number,
          scale: 2,
          thousandsSeparator: ",",
          padFractionalZeros,
          normalizeZeros: true,
          radix: ".",
          min: 0,
          max: max,
        },
      }}
      inputRef={ref}
      onAccept={(value, maskRef) => {
        let masked = value;
        let unmasked = maskRef.unmaskedValue;
        // Ensure leading zero before decimal (e.g. "$." → "$0.")
        if (masked === "$." || masked === ".") {
          masked = "$0.";
          unmasked = "0.";
        } else if (masked.startsWith("$.")) {
          masked = "$0" + masked.slice(1);
          unmasked = "0" + unmasked;
        }
        const isEmpty = !masked || !unmasked;
        onChange?.(isEmpty ? { masked: "", unmasked: "" } : { masked, unmasked });
      }}
    />
  );
});

AmountField.prototype = { rules: PropTypes.object };

export const RegularAmountField = ({ name, rules, disabled = false, textFieldProps = {} }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      disabled={disabled}
      render={({ field, fieldState }) => (
        <CustomTextField
          label="Base Rent"
          {...field}
          {...textFieldProps}
          errorMessage={fieldState?.error?.message}
          error={!!fieldState?.error}
          inputProps={{ mask: "num", padFractionalZeros: true }}
          InputProps={{
            inputComponent: AmountFieldMaskAdapter,
            startAdornment: (
              <InputAdornment position="start">
                <span className="text-lg-medium color--mist">$</span>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};
